// @flow
/** @jsx jsx */

import { pick } from 'lodash';

import useFormState from '@zapier/payment/src/utils/useFormState';
import { PaymentFormButtons } from '@zapier/payment';
import { jsx } from '@emotion/core';
import {
  type ApiKeys,
  type CreditCardBrandCode,
  type FieldName,
  type FieldValue,
  type HostedFieldAdditionalData,
  type HostedFieldToken,
  type PaymentDetailsCard,
  type PaymentProcessor,
  paymentSourceMethods,
} from '@zapier/payment/src/types';
import { AsyncOverlay, MessageBar } from '@zapier/common-components';

import CreditCardErrors from './CreditCardErrors';
import CreditCardFieldset from './CreditCardFieldset';
import { HostedFieldsProvider } from './HostedFields';

type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys,
  /** Payment processor to use */
  paymentProcessor: PaymentProcessor,
  /** Error message to show */
  error?: string,
  /** Show a cancel button */
  hasCancelButton?: boolean,
  /** User has saved payment source */
  hasPaymentSource: boolean,
  /** Editing is busy */
  isInFlight: boolean,
  /** Call to cancel current edit */
  onCancel: () => void,
  /** Called when card number changes */
  onCardNumberChange?: (type: ?CreditCardBrandCode) => void,
  /** Call when error happens during edit */
  onError: (error: Error) => void,
  /** Call to save current edit */
  onSave: (token: HostedFieldToken) => void,
  /** Saved credit card details */
  savedCard: ?PaymentDetailsCard,
};

const CreditCardForm = (props: Props) => {
  const {
    state,
    updateErrors,
    checkIsValid,
    clearErrors,
    getAdditionalData,
    getFieldsErrors,
    startTokenization,
    stopTokenization,
    updateField,
  } = useFormState(paymentSourceMethods.card, props.savedCard);

  const handleChange = (name: FieldName, value: FieldValue) => {
    // If changing card number state, trigger callback with card brand
    if (!!props.onCardNumberChange && name === 'number') {
      // $FlowFixMe
      props.onCardNumberChange(value.brand || null);
    }

    updateField(name, value);
  };

  const handleSave = (
    createToken: (
      additionalData: HostedFieldAdditionalData
    ) => Promise<HostedFieldToken>
  ) => async (event: SyntheticEvent<HTMLElement>) => {
    event.preventDefault();

    const isValid = checkIsValid();

    if (isValid) {
      startTokenization();

      try {
        const { token, details } = await createToken(getAdditionalData());
        clearErrors();

        const inputDetails = pick(details, [
          'exp_month',
          'exp_year',
          'address_city',
          'address_state',
          'address_zip',
          'address_country',
        ]);

        // Depending on the payment processor, method may or may not be present
        // Hardcode it here so we can use it for tracking
        props.onSave({
          details: inputDetails,
          token,
          method: paymentSourceMethods.card,
          paymentProcessor: props.paymentProcessor,
        });
      } catch (error) {
        clearErrors();
        props.onError(error);
      } finally {
        stopTokenization();
      }
    } else {
      updateErrors();
    }
  };

  const isBusy = props.isInFlight || state.isTokenizing;
  const hasServerError = !state.isDirty && props.error;
  const hasFormError = state.hasErrors;
  const submitText = `${props.hasPaymentSource ? 'Update' : 'Add'} Card`;

  return (
    <HostedFieldsProvider
      apiKeys={props.apiKeys}
      onChange={handleChange}
      paymentProcessor={props.paymentProcessor}
    >
      {createToken => (
        <AsyncOverlay isVisible={isBusy}>
          <form
            // Used by E2E tests
            name="cc-form"
            onSubmit={handleSave(createToken)}
          >
            {hasServerError ? (
              <MessageBar margin={{ bottom: 20 }} type="error">
                {props.error}
              </MessageBar>
            ) : (
              hasFormError && <CreditCardErrors errors={getFieldsErrors()} />
            )}
            <CreditCardFieldset fields={state.fields} onChange={handleChange} />
            <PaymentFormButtons
              buttonText={submitText}
              canSubmit={state.isDirty}
              hasCancelButton={props.hasCancelButton}
              isBusy={isBusy}
              onCancel={() => {
                // Clear card type when credit card form is not shown
                if (props.onCardNumberChange) {
                  props.onCardNumberChange(null);
                }
                props.onCancel();
              }}
            />
          </form>
        </AsyncOverlay>
      )}
    </HostedFieldsProvider>
  );
};

export type CreditCardFormProps = Props;
export default CreditCardForm;
